import React, { useEffect, useState } from "react";
import { fetchForgeAnalytics } from "@/lib/forgeApi";
import { Brain, Lightbulb, TrendingUp, TriangleAlert, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TAG_STYLES = {
  alpha: { icon: TrendingUp, color: "text-[#00C805]", bg: "bg-[#00C805]/8 border-[#00C805]/30" },
  warning: { icon: TriangleAlert, color: "text-[#FF7A33]", bg: "bg-[#FF7A33]/8 border-[#FF7A33]/30" },
  tip: { icon: Lightbulb, color: "text-[#FFD60A]", bg: "bg-[#FFD60A]/8 border-[#FFD60A]/30" },
  info: { icon: Info, color: "text-white/70", bg: "bg-white/5 border-white/15" },
};

const fmtPct = (n) => (n == null ? "—" : (n >= 0 ? "+" : "") + Number(n).toFixed(2) + "%");
const fmt$ = (n) =>
  n == null ? "—" : (n >= 0 ? "+" : "−") + "$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

export const AlphaInsights = ({ refreshKey }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    fetchForgeAnalytics()
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [user, refreshKey]);

  if (!user) {
    return (
      <div
        className="bg-[#0c0c0e] border border-white/10 rounded-xl p-5"
        data-testid="alpha-insights-locked"
      >
        <div className="flex items-center gap-2 mb-2">
          <Brain className="w-4 h-4 text-[#A98CFF]" />
          <h3 className="text-sm font-medium tracking-tight">Alpha Optimization Feed</h3>
        </div>
        <p className="text-[12px] text-white/50">
          Sign in and log strategies to unlock data-driven insights: per-playbook win rate,
          hold-time optimization, and "close at 50% profit" alerts based on your history.
        </p>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div
        className="bg-[#0c0c0e] border border-white/10 rounded-xl p-5 text-[11px] text-white/40"
        data-testid="alpha-insights-loading"
      >
        Computing your insights…
      </div>
    );
  }

  const overall = data.overall || {};
  return (
    <div
      className="bg-[#0c0c0e] border border-white/10 rounded-xl p-4 sm:p-5"
      data-testid="alpha-insights"
    >
      <div className="flex items-center gap-2 mb-3">
        <Brain className="w-4 h-4 text-[#A98CFF]" />
        <h3 className="text-sm font-medium tracking-tight">Alpha Optimization Feed</h3>
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">
          {overall.closed}/{overall.total} closed
        </span>
      </div>

      {/* Overall KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <Kpi label="Win rate" value={overall.win_rate == null ? "—" : `${overall.win_rate}%`}
             color={overall.win_rate >= 60 ? "text-[#00C805]" : overall.win_rate >= 40 ? "text-[#FFD60A]" : "text-[#FF7A33]"} />
        <Kpi label="Avg PnL %" value={fmtPct(overall.avg_pnl_pct)}
             color={(overall.avg_pnl_pct ?? 0) >= 0 ? "text-[#00C805]" : "text-[#FF3B30]"} />
        <Kpi label="Realized PnL" value={fmt$(overall.total_realized_pnl)}
             color={(overall.total_realized_pnl ?? 0) >= 0 ? "text-[#00C805]" : "text-[#FF3B30]"} />
        <Kpi label="Total logged" value={overall.total ?? 0} />
      </div>

      {/* Insights */}
      {!!data.insights?.length && (
        <div className="space-y-2 mb-4">
          {data.insights.map((ins, i) => {
            const st = TAG_STYLES[ins.tag] || TAG_STYLES.info;
            const Icon = st.icon;
            return (
              <div
                key={i}
                className={`rounded-lg border px-3 py-2.5 ${st.bg}`}
                data-testid={`alpha-insight-${i}`}
              >
                <div className="flex items-start gap-2">
                  <Icon className={`w-3.5 h-3.5 mt-0.5 ${st.color}`} />
                  <div>
                    <div className={`text-[12px] font-medium ${st.color}`}>{ins.title}</div>
                    <div className="text-[11px] text-white/70 mt-0.5 leading-snug">{ins.body}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* By Playbook table */}
      {!!data.by_playbook?.length && (
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-white/[0.02]">
              <tr className="text-white/45 uppercase tracking-[0.16em] text-[9px]">
                <th className="text-left px-3 py-2">Playbook</th>
                <th className="text-right px-3 py-2">N</th>
                <th className="text-right px-3 py-2">Closed</th>
                <th className="text-right px-3 py-2">Win %</th>
                <th className="text-right px-3 py-2">Avg PnL %</th>
                <th className="text-right px-3 py-2">Hold (d)</th>
              </tr>
            </thead>
            <tbody>
              {data.by_playbook.map((p) => (
                <tr key={p.playbook} className="border-t border-white/5">
                  <td className="px-3 py-1.5 text-white/80">{p.playbook}</td>
                  <td className="px-3 py-1.5 text-right num text-white/60">{p.count}</td>
                  <td className="px-3 py-1.5 text-right num text-white/60">{p.closed}</td>
                  <td className={`px-3 py-1.5 text-right num font-medium ${
                    p.win_rate >= 60 ? "text-[#00C805]" : p.win_rate >= 40 ? "text-[#FFD60A]" : "text-[#FF7A33]"
                  }`}>{p.closed ? `${p.win_rate}%` : "—"}</td>
                  <td className={`px-3 py-1.5 text-right num ${
                    (p.avg_pnl_pct ?? 0) >= 0 ? "text-[#00C805]" : "text-[#FF3B30]"
                  }`}>{p.closed ? fmtPct(p.avg_pnl_pct) : "—"}</td>
                  <td className="px-3 py-1.5 text-right num text-white/60">{p.avg_hold_days ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, color = "text-white" }) => (
  <div className="bg-[#0a0a0c] border border-white/10 rounded-lg px-3 py-2.5">
    <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">{label}</div>
    <div className={`num text-base font-medium mt-0.5 ${color}`}>{value}</div>
  </div>
);

export default AlphaInsights;
